import qs from 'query-string'
import { useQueryClient } from 'react-query'

import { useQueryApi, useMutationApi } from 'utils/api'
import {
  handleError,
  validateStatus,
} from 'api/kinerja/pengawas-sekolah/config'
import { apiPaths, queryKeys } from './config'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { AxiosError } from 'axios'
import type {
  ResponseGetPenetapanPegawai,
  PenetapanPegawaiFilters,
  RequestSendPenetapanPegawai,
  RequestSavePenetapanPegawai,
  ParamsGetPenetapanPegawaiDetail,
  PenetapanPegawaiDetail,
  PenetapanPegawaiCountStatus,
  PenetapanPegawaiCountPredicate,
} from 'types/kinerja-tim-penilai-ps'
import type { PenetapanPegawaiState } from 'types/kinerja-ps-pegawai'
import type { UseQueryOptions, UseMutationOptions } from 'react-query'
import type { CustomError } from 'utils/customError'

export function useAPIGetPenetapanPegawai({
  params,
  options,
}: {
  params?: PenetapanPegawaiFilters & {
    offset?: number
    limit?: number
  }
  options?: UseQueryOptions<ResponseGetPenetapanPegawai>
}) {
  const { period } = usePeriodContext()
  const stringifiedQuery = qs.stringify(params)
  return useQueryApi<ResponseGetPenetapanPegawai>(
    `${apiPaths.penetapan(period.id)}?${stringifiedQuery}`,
    {
      queryKey: [
        queryKeys.API_GET_PENETAPAN_PEGAWAI,
        {
          period: period.id,
          params,
        },
      ],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: false,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIGetPenetapanPegawaiCountStatus({
  options,
}: {
  options?: UseQueryOptions<PenetapanPegawaiCountStatus>
}) {
  const { period } = usePeriodContext()
  return useQueryApi<PenetapanPegawaiCountStatus>(
    `${apiPaths.penetapan(period.id)}/count/status`,
    {
      queryKey: [
        queryKeys.API_GET_PENETAPAN_PEGAWAI_COUNT_STATUS,
        {
          period: period.id,
        },
      ],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: false,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIGetPenetapanPegawaiCountPredicate({
  params,
  options,
}: {
  params?: {
    status: PenetapanPegawaiState
  }
  options?: UseQueryOptions<PenetapanPegawaiCountPredicate>
}) {
  const { period } = usePeriodContext()
  const stringifiedQuery = qs.stringify(params)
  return useQueryApi<PenetapanPegawaiCountPredicate>(
    `${apiPaths.penetapan(
      period.id
    )}/count/predikat-kinerja?${stringifiedQuery}`,
    {
      queryKey: [
        queryKeys.API_GET_PENETAPAN_PEGAWAI_COUNT_PREDICATE,
        {
          period: period.id,
          params,
        },
      ],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: false,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPIGetPenetapanPegawaiDetail({
  params,
  options,
}: {
  params: ParamsGetPenetapanPegawaiDetail
  options?: UseQueryOptions<PenetapanPegawaiDetail>
}) {
  const { period } = usePeriodContext()
  const { pengawasSekolahId } = params

  return useQueryApi<PenetapanPegawaiDetail>(
    `${apiPaths.penetapan(period.id)}/ps/${pengawasSekolahId}`,
    {
      queryKey: [
        queryKeys.API_GET_PENETAPAN_PEGAWAI_DETAIL,
        {
          period: period.id,
          pengawasSekolahId,
        },
      ],
      axiosConfig: {
        validateStatus,
      },
      queryConfig: {
        useErrorBoundary: true,
        ...options,
      },
      errorHandlerFn: handleError,
    }
  )
}

export function useAPISavePenetapanPegawai({
  options,
}: {
  options?: UseMutationOptions<
    unknown,
    CustomError,
    RequestSavePenetapanPegawai
  >
}) {
  const { period } = usePeriodContext()

  return useMutationApi<unknown, CustomError, RequestSavePenetapanPegawai>(
    apiPaths.penetapan(period.id),
    {
      axiosConfig: {
        method: 'POST',
      },
      mutationConfig: {
        ...options,
      },
    }
  )
}

export function useAPISendPenetapanPegawaiToPPK({
  options,
}: {
  options?: UseMutationOptions<
    unknown,
    CustomError,
    RequestSendPenetapanPegawai
  >
}) {
  const { period } = usePeriodContext()

  return useMutationApi<unknown, CustomError, RequestSendPenetapanPegawai>(
    `${apiPaths.penetapan(period.id)}/send-to-ppk`,
    {
      axiosConfig: {
        method: 'POST',
      },
      mutationConfig: {
        ...options,
      },
    }
  )
}

export function useAPISendPenetapanPegawaiToEkin({
  options,
}: {
  options?: UseMutationOptions<
    unknown,
    CustomError,
    RequestSendPenetapanPegawai
  >
}) {
  const { period } = usePeriodContext()

  return useMutationApi<unknown, CustomError, RequestSendPenetapanPegawai>(
    `${apiPaths.penetapan(period.id)}/send-to-ekin`,
    {
      axiosConfig: {
        method: 'POST',
      },
      mutationConfig: {
        ...options,
      },
    }
  )
}

export function useAPIResetPenetapan({
  pengawasSekolahId,
}: {
  pengawasSekolahId: string
}) {
  const { period } = usePeriodContext()
  const queryClient = useQueryClient()

  return useMutationApi<{ message: string }, AxiosError, {}>(
    `${apiPaths.penetapan(period.id)}/ps/${pengawasSekolahId}/reset`,
    {
      axiosConfig: { method: 'POST' },
      mutationConfig: {
        onSettled() {
          queryClient.invalidateQueries([
            queryKeys.API_GET_PENETAPAN_PEGAWAI_DETAIL,
            {
              period: period.id,
              pengawasSekolahId,
            },
          ])
          queryClient.invalidateQueries([
            queryKeys.API_GET_PENETAPAN_PEGAWAI,
            {
              period: period.id,
            },
          ])
          queryClient.invalidateQueries([
            queryKeys.API_GET_PENETAPAN_PEGAWAI_COUNT_STATUS,
            {
              period: period.id,
            },
          ])
          queryClient.invalidateQueries([
            queryKeys.API_GET_PENETAPAN_PEGAWAI_COUNT_PREDICATE,
            {
              period: period.id,
            },
          ])
        },
      },
    }
  )
}
